import type { FaqDisplayType, FaqDto, FaqMetaEntry } from "../types"
import { getLocalizedText } from "../utils/getLocalizedText"

const SUPPORTED_LOCALES = ["en", "vi"]

/**
 * Không khai báo meta cho page => mặc định show ở mọi locale.
 * Có khai báo thì active:false cho locale nào sẽ ẩn locale đó (ưu tiên hơn active:true).
 */
export function isFaqVisibleAtPosition(faq: FaqDto, position: string, locale: string): boolean {
  const entries = (faq.meta || []).filter((entry) => entry.page === position)
  if (entries.length === 0) return true

  const isDeactivated = entries.some(
    (entry) => entry.active === false && entry.locale?.includes(locale)
  )
  return !isDeactivated
}

/**
 * Meta cho phép hiển thị nhưng question/answer chưa dịch (rỗng/undefined) ở locale
 * đó thì vẫn phải ẩn, tránh render row trống.
 */
export function hasLocalizedFaqContent(faq: FaqDto, locale: string): boolean {
  return Boolean(getLocalizedText(faq.question, locale) && getLocalizedText(faq.answer, locale))
}

export function filterFaqsByMeta(faqs: FaqDto[], position: string, locale: string): FaqDto[] {
  return faqs.filter(
    (faq) => isFaqVisibleAtPosition(faq, position, locale) && hasLocalizedFaqContent(faq, locale)
  )
}

/**
 * BE trả displayType không đồng nhất hoa/thường ("InfoCard", "INFO_CARD",
 * "image_card"...) — chuẩn hoá về 1 trong 3 giá trị cố định, thiếu/không khớp = LIST.
 */
export function normalizeFaqDisplayType(raw?: string | null): FaqDisplayType {
  const key = (raw || "").trim().toUpperCase().replace(/[^A-Z]/g, "")
  if (key === "INFOCARD") return "INFO_CARD"
  if (key === "IMAGECARD") return "IMAGE_CARD"
  return "LIST"
}

export type FaqDisplayMeta = {
  displayType: FaqDisplayType
  image?: string
  description?: string
}

function findWinningMetaEntry(faq: FaqDto, position: string, locale: string): FaqMetaEntry | undefined {
  return (faq.meta || []).find(
    (entry) => entry.page === position && entry.locale?.includes(locale) && entry.active !== false
  )
}

/** Lấy displayType/image/description áp dụng cho 1 FAQ tại đúng page+locale hiện tại. */
export function getFaqDisplayMeta(faq: FaqDto, position: string, locale: string): FaqDisplayMeta {
  const entry = findWinningMetaEntry(faq, position, locale)
  return {
    displayType: normalizeFaqDisplayType(entry?.displayType),
    image: entry?.image || undefined,
    description: entry?.description || undefined,
  }
}

export function getAvailableLocales(faqs: FaqDto[], position: string): string[] {
  const locales = new Set<string>()

  faqs.forEach((faq) => {
    SUPPORTED_LOCALES.forEach((locale) => {
      if (isFaqVisibleAtPosition(faq, position, locale) && hasLocalizedFaqContent(faq, locale)) {
        locales.add(locale)
      }
    })
  })

  return locales.size > 0 ? Array.from(locales) : SUPPORTED_LOCALES
}
